import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";

// Global render-crash boundary (2026-11-03).
//
// WHY THIS EXISTS: until now the SPA had NO error boundary anywhere — any
// uncaught exception during render (e.g. a component reading `data.x` on an
// undefined query result after a failed fetch) unmounted the ENTIRE React
// tree → blank white page, no message, no recovery. The API layer's toasts
// (api.ts) cover request failures, but a render-time crash bypasses them.
//
// Placement: main.tsx wraps QueryClientProvider/AuthProvider/App in this
// boundary (OUTSIDE AuthProvider so an AuthProvider crash — e.g. the
// VITE_APPWRITE_* missing-env class documented in SCHEMA.md §12 — is also
// caught, which previously blank-paged the whole app).
//
// Recovery: a full-page reload. NOT a setState reset — React has already
// unwound the crashed subtree, so its local state is gone and a re-render
// would crash again at the same spot. location.reload() is honest: the
// httpOnly session cookie survives (auth state is server-managed, §11) and
// the PWA re-registers on load, so the user lands signed-in on the same
// route. The reload also re-runs the nuke-on-update SW checks (PwaUpdater).
//
// Reporting: console.error only (message + componentStack). The server-side
// error_log table captures API 4xx/5xx — a CLIENT render crash has no server
// row; surfacing it to the server would need a POST endpoint (deferred —
// the browser console + the fallback page is the current signal).

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Full stack + component trace in the browser console — the client-side
    // counterpart of the Worker's [uncaught] observability log.
    console.error("[ErrorBoundary]", error.message, error.stack, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-cream-50 p-8 text-center">
        <h1 className="font-serif text-2xl text-maroon-700">Something went wrong</h1>
        <p className="max-w-md text-sm text-ink">
          An unexpected error occurred and the page could not be shown. Your data is saved on the
          server — reloading the page usually fixes this.
        </p>
        <p className="max-w-md break-words text-xs text-muted">
          {this.state.error.message}
        </p>
        <Button onClick={() => window.location.reload()}>Reload the page</Button>
      </div>
    );
  }
}

export default ErrorBoundary;